import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import { LifeBuoy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

import { SUPPORT_REPORT_KIND_OPTIONS } from "@/constants/support/support-constants";
import { useSendSupportReport } from "@/hooks/support/use-support-reports";
import { getErrorMessage } from "@/lib/handle-mutation-error";
import type { SupportReportKind } from "@/types/support/api-types/support-report-types";
import { supportReportSchema } from "@/zod-schemas/support/support-report-schema";

export function SupportDialog() {
  const [open, setOpen] = useState(false);
  const sendReport = useSendSupportReport();

  const form = useForm({
    defaultValues: { kind: "bug" as SupportReportKind, message: "" },
    validators: { onSubmit: supportReportSchema },
    onSubmit: async ({ value }) => {
      try {
        // Validators don't transform the form's value: parse to get the trimmed request body.
        const sent = await sendReport.mutateAsync(supportReportSchema.parse(value));
        toast.success(`Sent - we'll be in touch. Ref #${sent.github_issue_number}.`);
      } catch (error) {
        toast.error(getErrorMessage(error));
        return;
      }
      form.reset();
      setOpen(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <LifeBuoy />
          Support
        </Button>
      </DialogTrigger>
      {/* No description: the form says it all (Radix's documented opt-out). */}
      <DialogContent className="sm:max-w-[480px]" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Contact support</DialogTitle>
        </DialogHeader>

        <form
          id="support-report-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="kind"
              children={(field) => (
                <FieldSet>
                  <FieldLegend variant="label">What's it about?</FieldLegend>
                  <RadioGroup
                    name={field.name}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value as SupportReportKind)}
                    className="grid-cols-2 sm:grid-cols-4"
                  >
                    {SUPPORT_REPORT_KIND_OPTIONS.map((option) => (
                      <Field key={option.value} orientation="horizontal">
                        <RadioGroupItem value={option.value} id={`support-kind-${option.value}`} />
                        <FieldLabel htmlFor={`support-kind-${option.value}`} className="font-normal">
                          {option.label}
                        </FieldLabel>
                      </Field>
                    ))}
                  </RadioGroup>
                </FieldSet>
              )}
            />

            <form.Field
              name="message"
              children={(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>What's going on?</FieldLabel>
                    <Textarea
                      id={field.name}
                      name={field.name}
                      rows={5}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="e.g. The calendar won't save a booking for Friday"
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                );
              }}
            />
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <form.Subscribe
            selector={(state) => state.isSubmitting}
            children={(isSubmitting) => (
              <Button type="submit" form="support-report-form" className="w-24" disabled={isSubmitting}>
                {isSubmitting ? "Sending…" : "Send"}
              </Button>
            )}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
